import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { FoodDbService } from 'services/food-db.service';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { DocWatcher } from 'services/database-wrapper';
import { Food, FoodHeader, NutrientTargets, ComplexFood, getIngredientText, AnyFood, isLogMeal } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { ImageService } from 'services/image-service';
import { tunableTargetsOr } from '@cf-shared/meal-targets-as-if-empty';
import { MealTuner } from '@cf-shared/meal-tuner';
import { MealTunerMeal, MealTunerOutcome, MealTunerParams } from '@cf-shared/meal-tuner-interface';
import { reportInvalidTunerMeals } from 'util/cannot-tune-reason';
import { SettingsService } from 'services/settings.service';
import { ToastController } from 'providers/toast-controller';
import { TelemetryService } from 'services/telemetry.service';
import { DebugService } from 'services/debug.service';
import { LoggingService } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { noteSomethingWentWrong } from 'services/session-health';
import { FormatNutrients, formatRelativeDay, formatScheduledMealName } from 'util/formatting';
import { UserDb } from 'services/user-db.service';
import { LikeState } from 'components/cs-food-card/cs-food-card';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { IEditFoodPage } from 'pages/edit-food/edit-food-interface';
import { MealSuggestionsPageParams, MealSuggestionsMode } from './meal-suggestions-interface';
import { dedupeCandidatesByFingerprint, readSuggestionFoodsAsync, selectSuggestedMeals } from './meal-suggestion-selection-policy';

// Used internally to display the suggestions on this page only.
class MealSuggestionEntry {
  num: number;
  name: string;
  image: string;
  match: number;
  food: AnyFood;
  displayNutrients: UiDisplayNutrient[];
  tunerSuggestion: MealTunerMeal;
  // built from food/ingredient names (database content), not translatable catalog text
  ingredientText: TranslatedString;
}

@Component({
    selector: 'page-meal-suggestions',
    templateUrl: 'meal-suggestions.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    providers: [ModalPageMarker]
})
export class MealSuggestionsPage extends CsPage<MealSuggestionsPageParams> implements OnDestroy {
  protected LL = LL;
  title: string;
  meals: MealSuggestionEntry[] = [];
  targets: NutrientTargets;
  targetDisplayNutrients: UiDisplayNutrient[];
  loading = false;
  somethingAccepted = false; // used to notify of canceling
  spawningChildPage = false; // used to prevent ionViewCanLeave() on viewing recipe etc.
  selectedMealIndex: number | null = null;
  mode: MealSuggestionsMode = MealSuggestionsMode.Add;
  private docWatcher: DocWatcher;

  protected LikeState = LikeState;

  get actionButtonLabel(): TranslatedString {
    return this.mode === MealSuggestionsMode.Replace
      ? LL.mealSuggestions.buttons.replace()
      : LL.mealSuggestions.buttons.addMeal();
  }

  constructor(
    public navCtrl: NavController,
    public cloudFunctionsService: CloudFunctionsService,
    private toastCtrl: ToastController,
    private foodDb: FoodDbService,
    protected imageService: ImageService,
    protected settings: SettingsService,
    private telemetry: TelemetryService,
    private userDb: UserDb,
    private popupMenu: PopupMenuProvider,
    private cdRef: ChangeDetectorRef,
    private debug: DebugService,
    private logging: LoggingService,
    private databaseRecovery: DatabaseRecoveryService
  ) {
    super();
    this.title = LL.mealSuggestions.title();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.foodDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdRef.markForCheck();
      });
    }

    this.mode = this.navParams.data.mode || MealSuggestionsMode.Add;
    // if replace mode, use "Replace Meal" title; otherwise use provided title
    this.title = this.mode === MealSuggestionsMode.Replace
      ? LL.mealSuggestions.replaceMealTitle()
      : this.navParams.data.title;
    void this.suggestTunerAsync();
  }

  async ionViewDidEnter() {
    this.spawningChildPage = false; // this is needed when returning from e.g. view recipe page
  }

  async suggestTunerAsync() {
    this.loading = true;

    try {
      this.targets = this.navParams.data.local?.targets;

      // gate on auto-plan recipe cache (see awaitAutoPlanFoodsCachedWithTimeoutAsync
      // for rationale — solver throws on missing ingredient food, #3244).
      const cacheReady = await this.foodDb.awaitAutoPlanFoodsCachedWithTimeoutAsync(60_000);
      if (!cacheReady) {
        this.toastCtrl.showToast({
          message: LL.mealSuggestions.errors.recipeLibraryNotReady(),
          showCloseButton: true,
          duration: 10_000,
        });
        void this.navCtrl.popAsync();
        return;
      }

      // Since we cache global recipes and foods at app startup we only need to seach from cache to avoid long loading times from fetching same recepies again from remote database.
      const allCandidates = await this.foodDb.getMealFoodCandidatesAsync(this.docWatcher, { searchOnlyLocal: true });

      // de-duplicate by ingredient fingerprint — recipes take priority over Log_Meals
      const candidates = dedupeCandidatesByFingerprint(allCandidates, food => this.foodDb.getIngredientFingerprint(food));

      let tunerParams: MealTunerParams = await this.foodDb.prepareTuningParamsFoodsAsync(candidates as ComplexFood[]);
      // these are candidates to pick from, so one with broken numbers is reported, not a crash
      tunerParams.candidates = true;
      // if the tuning targets have no usable calorie target (can happen with calorie cycling
      // mismatch, or on a day whose calories are already spent), fall back to the meal's own plan
      // figure -- `tunableTargetsOr` is the one place that rule lives.
      tunerParams.targets = tunableTargetsOr(this.navParams.data.local?.tuningTargets, this.targets);

      let tuner = new MealTuner();

      let results = tuner.solve(tunerParams);

      // the tuner crashed. it catches everything it throws and answers with an empty meal list
      // plus a message, so reading the list alone showed the user an empty suggestions page with
      // no explanation, and left the crash itself with no trace at all.
      //
      // asked through `outcome`: a failed solve carries no meal list at all, so reading the answer
      // below without this check does not compile.
      //
      // thrown rather than handled here, because the catch at the bottom of this method is
      // already the one place that decides what the user is told when this page cannot build its
      // list -- and it reports to Sentry and leaves the page, which is exactly what a crash
      // deserves. an empty list that is NOT a crash still falls through to the honest empty page.
      if (results.outcome === MealTunerOutcome.Failed) {
        throw new Error(`MealTuner.solve failed: ${results.error}`);
      }

      // some candidates were left out because their numbers are broken. the rest were still
      // tuned and are suggested as usual; this only makes sure the broken ones leave a trace.
      reportInvalidTunerMeals(this.logging, results, 'meal-suggestions');

      // the top results, plus recent Log_Meals that fit very well and aren't already represented
      const finalResults = selectSuggestedMeals(results.meals, candidates);

      this.targetDisplayNutrients = FormatNutrients({}, this.userDb, { targets: this.targets, onlyTargets: true });

      let meals: MealSuggestionEntry[] = [];
      let i = 1;
      // a suggestion whose food cannot be read is left out and reported, rather than taking the
      // whole list down with it (df-721047a6faf6)
      const readable = await readSuggestionFoodsAsync(
        finalResults,
        id => this.foodDb.getFoodOrLogMealByIdAsync(id, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher),
        this.logging,
      );
      for (let { meal, food } of readable) {

        // for Log_Meal, show base recipe name or meal name with date
        if (isLogMeal(food)) {
          const displayName = await this.getLogMealDisplayNameAsync(food);
          food = { ...food, name: displayName };
        }

        let image = (food as Food).image ? this.imageService.getImageThumbnailUrl((food as Food).image) : null;

        // generate ingredient text from recipe ingredients (both ComplexFood and Log_Meal have ingredients)
        let ingredientText = untranslatedString(getIngredientText((food as ComplexFood).ingredients, '; '));

        let suggestion: MealSuggestionEntry = {
          num: i++,
          name: food.name,
          image: image,
          food: food,
          tunerSuggestion: meal,
          match: Math.round(meal.precision_pc),
          displayNutrients: FormatNutrients(meal.nutrients, this.userDb, { targets: this.targets }),
          ingredientText: ingredientText,
        };

        meals.push(suggestion);
      }

      this.meals = meals;
    } catch (err) {
      // a bare console.error reached neither Sentry nor anybody who could act on it, so every
      // failure that stops this page building its list -- the tuner crashing, a food that cannot
      // be read -- was visible only to somebody with a browser console open.
      //
      // if the local database itself broke, hand it to recovery and stop. `trackAbnormalException`
      // used to run this check for us as its first step, and the report below does not -- the same
      // migration, and the same note, as `user-profile.ts`'s account-purge catch. This method
      // reads a lot of the local database, so it is a real route in.
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        if (!this.databaseRecovery.isIntentionalDestruction()) noteSomethingWentWrong();
        return;
      }

      // reported and toasted SEPARATELY, rather than by handing the wording to
      // `trackAbnormalException`, because that method returns before it shows anything on three
      // further paths: a cloud-function network error, a retriable 5xx, and a report the throttler
      // silenced. This page always leaves as it reports, so on any of those the user would be
      // dropped back with nothing said at all -- which is the silence this whole change is about.
      this.logging.trackSilentProblem('meal_suggestions_failed', {
        error: describeErrorForLog(err),
      });
      void this.navCtrl.popAsync();
      this.toastCtrl.showToast({
        message: LL.mealSuggestions.errors.serviceUnavailable(),
        showCloseButton: true,
        duration: 10000,
      });
    } finally {
      this.loading = false;
      this.safeMarkForCheck(this.cdRef);
    }
  }

  /** Set vote up/down */
  async voteAsync(food: Food, direction: -1 | 1) {
    if (food) {
      food.local = food.local || {};
      this.foodDb.voteFood(food, direction);
      let saving = this.foodDb.saveUserFoodAsync(food, direction === -1 ? 'cached' : 'used');

      // if downvoted -  remove its suggestion from the current list
      if (food.local && food.local.liked < 0) {
        this.meals = this.meals.filter(m => m.food._id !== food._id);
      }
      this.cdRef.markForCheck();
      this.telemetry.trackEvent('action_mealsuggestions_vote', { direction });

      return saving; // so someone can wait if needed somewhere
    }
    return null;
  }

  handleViewRecipe(recipeId: string) {
    void this.viewRecipeAsync(recipeId);
  }
  async viewRecipeAsync(recipeId: string) {
    this.spawningChildPage = true; // prevent ionViewCanLeave() from triggering here
    void this.navCtrl.pushAsync(IEditFoodPage, { id: recipeId, mode: 'view' });
    this.telemetry.trackEvent('action_mealsuggestions_viewrecipe', {});
  }

  handleAcceptMeal(meal: MealSuggestionEntry) {
    void this.acceptMealAsync(meal);
    this.telemetry.trackEvent('action_mealsuggestions_accept', {});
  }
  async acceptMealAsync(meal: MealSuggestionEntry) {
    this.somethingAccepted = true;
    // pass the selected meal as result - NavController will invoke returnCallback automatically
    await this.navCtrl.popAsync(meal.tunerSuggestion);
  }

  // Note: Callbacks are now handled via the returnCallback mechanism in NavController.
  // When user cancels (back button), NavController invokes returnCallback with undefined result.
  // The somethingAccepted flag is set in acceptMealAsync before popping with the result.

  handleMealSelected(index: number) {
    this.selectedMealIndex = index;
  }

  handleAcceptSelectedMeal(event: Event) {
    if (this.selectedMealIndex !== null && this.meals[this.selectedMealIndex]) {
      const meal = this.meals[this.selectedMealIndex];
      this.telemetry.trackEvent('action_mealsuggestions_accept', {});
      lockClick(event, this.acceptMealAsync(meal));
    }
  }

  handleLike(meal: MealSuggestionEntry) {
    // can only vote on Food objects, not Log_Meal
    if (!isLogMeal(meal.food)) {
      void this.voteAsync(meal.food as Food, +1);
    }
  }

  handleDislike(meal: MealSuggestionEntry) {
    // can only vote on Food objects, not Log_Meal
    if (!isLogMeal(meal.food)) {
      void this.voteAsync(meal.food as Food, -1);
    }
    // if meal was selected and is now removed, clear selection
    if (this.selectedMealIndex !== null) {
      const selectedMeal = this.meals[this.selectedMealIndex];
      if (selectedMeal && selectedMeal.food._id === meal.food._id) {
        this.selectedMealIndex = null;
      }
    }
  }

  handleCancel(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  getLikeState(meal: MealSuggestionEntry): LikeState {
    // Log_Meal doesn't have local.liked
    if (isLogMeal(meal.food)) {
      return LikeState.None;
    }
    const liked = (meal.food as Food)?.local?.liked;
    if (liked === 1) return LikeState.Liked;
    if (liked === -1) return LikeState.Disliked;
    return LikeState.None;
  }

  /** Get display name for a Log_Meal - shows base recipe name or meal name with date */
  private async getLogMealDisplayNameAsync(logMeal: Log_Meal): Promise<string> {
    // if based on a recipe/food, show that food's name
    if (logMeal.basedOnFoodId) {
      // basedOnFoodId can be either a recipe ID (food/) or another Log_Meal ID (log/)
      const baseFood = await this.foodDb.getFoodOrLogMealByIdAsync(logMeal.basedOnFoodId, { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }, this.docWatcher);
      if (baseFood) {
        return baseFood.name;
      }
    }
    // otherwise show meal name + relative date
    const mealTime = getEntryLocalTimeInDesiredTimeZone(logMeal, this.debug.getNowLocal());
    const dateLabel = formatRelativeDay(this.debug.getNowLocal(), mealTime);
    return `${formatScheduledMealName(logMeal.name)} (${dateLabel})`;
  }

  async handleMealMenu(event: { event: Event; food: FoodHeader }, meal: MealSuggestionEntry) {
    const targetElement = event.event.target as HTMLElement;
    await this.popupMenu.createMenu({
      buttons: [
        {
          text: LL.mealSuggestions.buttons.viewRecipe(),
          icon: 'information-circle',
          handler: () => this.handleViewRecipe(meal.food._id)
        }
      ],
      targetFromElement: targetElement
    });
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }
}
